import sgWildsComponent from "components/wilds/sg-wilds-component";
import Components from "slotworks/components/components";
import gameState from "slotworks/model/game-state";
import { ExpansionResult } from "slotworks/model/gameplay/records/results/expansion-result";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import { Services } from "slotworks/services/services";
import ExpandSymbols from "slotworks/state-machine/standard/states/expand-symbols";
import Contract from "slotworks/utils/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Sequence from "slotworks/utils/contracts/sequence";
import backgroundTint from "components/show-win-lines/background-tint";
import FreespinRecord from "slotworks/model/gameplay/records/freespin-record";

export default class SGExpandWilds extends ExpandSymbols {

    private expandSequences: Array<Contract<void>> = [];
    private wildExpandSounds: string[] = ["ra-expand", "maat-expand", "khnuhm-expand"];
    private combineSound: string = "stack-merge";
    private expandSound: string;
    private baseGameSounds = {
        decrease: "base-game-sound-decrease",
        normalise: "base-game-sound-normalise"
    };
    private bonusGameSounds = {
        decrease: "bonus-game-sound-decrease",
        normalise: "bonus-game-sound-normalise"
    };
    public onEnter() {
        const record = gameState.getCurrentGame().getCurrentRecord() as SpinRecord;
        backgroundTint.show();
        backgroundTint.updateDepth(record.results);
        const sound = this.isFreeSpin() ? this.bonusGameSounds.decrease : this.baseGameSounds.decrease;
        Services.sound.customEvent(sound);

        const expansionResults: ExpansionResult[] = record.getResultsOfType(ExpansionResult);
        this.expandSound = this.getCorrectExpandSound(expansionResults);
        if (expansionResults.length) {
            super.onEnter();
        } else {
            sgWildsComponent.clearExpandedReels();
            super.complete();
        }
    }

    public onExit(): void {
        const sound = this.isFreeSpin() ? this.bonusGameSounds.normalise : this.baseGameSounds.normalise;
        Services.sound.customEvent(sound);
    }

    public onSkip(): void {
        sgWildsComponent.skip();

        for (const expandSequence of this.expandSequences) {
            expandSequence.cancel();
        }

        Services.sound.customEvent("skip_expands");
        this.expandsComplete();

    }

    protected expand(expansion: ExpansionResult): Contract<void> {
        const sourceSymbol = Components.matrix.getSymbol(expansion.source.x, expansion.source.y);
        if (!sgWildsComponent.isExpandedReel(expansion.source.x)) {
            sourceSymbol.setVisible(false);
            const expandSequence = new Sequence<void>([
                () => new Parallel([
                    () => new Sequence([
                        () => Contract.getTimeoutContract(500),
                        () => Contract.wrap(() => Services.sound.customEvent(this.expandSound))
                    ]),
                    () => sgWildsComponent.playExpendAnimation(expansion.source)
                ]),
                () => Contract.getTimeoutContract(1000)
            ]);
            this.expandSequences.push(expandSequence);
            return expandSequence;
        }
        return Contract.empty();
    }

    private getCorrectExpandSound(expansionResults: ExpansionResult[]): string {
        if (expansionResults.length > 1) {
            return this.combineSound;
        } else {
            const reel: number = expansionResults[0].source.x;
            return this.wildExpandSounds[reel - 1];
        }
    }

    protected expandsComplete(): void {
        if (!this.isComplete) {
            this.expandSequences = [];
            this.isComplete = true;

            const record = gameState.getCurrentGame().getCurrentRecord() as SpinRecord;
            const expansionResults: ExpansionResult[] = record.getResultsOfType(ExpansionResult);

            for (const expansion of expansionResults) {
                const reel = expansion.source.x;

                Components.matrix.getReels()[reel].setVisible(false);

                const topSymbol = Components.matrix.getSymbol(reel, 0);

                // Dont override existing combined stickies
                const existingSticky = Components.matrix.getStuckSymbol(topSymbol.gridPosition);
                if (existingSticky && (existingSticky.symbolId.indexOf("2_") > -1 || existingSticky.symbolId.indexOf("3_") > -1)) {
                    continue;
                }

                topSymbol.setSymbol("expanded_" + (reel + 1));
                topSymbol.setVisible(false);
                topSymbol.static();

                sgWildsComponent.showStaticWild(reel);

                const masterSticky = Components.matrix.addStuckSymbol(topSymbol);

                for (let row = 1; row < 3; row++) {
                    const symbol = Components.matrix.getSymbol(reel, row);
                    symbol.setSymbol("expanded_" + (reel + 1));
                    symbol.setVisible(false);
                    symbol.static();

                    const stuckSymbol = Components.matrix.addStuckSymbol(symbol);
                    stuckSymbol.setIsStackPart(true);
                    stuckSymbol.setProxy(masterSticky);
                }
            }


            this.complete();
        }
    }

    private isFreeSpin(): boolean {
        const record = gameState.getCurrentGame().getCurrentRecord();
        return record instanceof FreespinRecord;
    }
}
